import Preact from 'preact';
import { QuestionCard, ConfigProductCard, CardPanel } from '@dora/components';
import * as Hooks from 'preact/hooks';
import * as Selectors from '../../store/selectors';
import * as ConfigurationActions from '../../store/actions/configuration';
import { ConfiguratorDefinition } from './configurationDefinition';
import { useSelector } from '../hooks/redux';
import { Price } from '@dora/backpack';
import { ActivePage } from '@dora/components/src/O_CardPanel';
import { TranslateContext } from '@denysvuika/preact-translate';
import { useDispatch } from '../hooks/redux';
import { PropertyValue } from './types';

export function Component(props: {
    onUpdateSelection: (propertyName: string, propertyValue: PropertyValue) => void;
    onUndo?: () => void;
    onRedo?: () => void;
    selections: Record<string, PropertyValue> | undefined;
    existingPropertyOptions: Record<string, Array<PropertyValue>> | undefined;
    compatiblePropertyOptions: Record<string, Array<PropertyValue>> | undefined;
    startWithSelections?: Record<string, PropertyValue>;
    addToCart: () => void;
    hideAlert: () => void;
    priceDeltas: Record<string, Record<string, number>>;
    requestPriceDelta: (propertyName: string, propertyValue: PropertyValue) => void;
    configuratorDefinition: ConfiguratorDefinition;
}) {
    const [activePage, setActivePage] = Hooks.useState<ActivePage>(ActivePage.QUESTIONS);
    const [currentProperty, setCurrentProperty] = Hooks.useState<null | string>(null);

    const locale = useSelector(Selectors.Core.locale);
    const currency = useSelector(Selectors.Core.currency);
    const isSafari = useSelector(Selectors.Core.isSafari);
    const dispatch = useDispatch();
    const { t } = Hooks.useContext(TranslateContext);

    const getOptionDisplayDetails = Hooks.useCallback(
        (
            property: string,
            option: PropertyValue,
        ): { question: string; displayText: string; displayImage: string } => {
            const lookup = option === null ? 'null' : option;

            const propertyDefinition = (props.configuratorDefinition as ConfiguratorDefinition)
                .configurableProperties[property];
            const question = propertyDefinition.displayName;

            // use the display text if available, or use defaultEmptySelection text if null and doesn't exist, or use the raw option as a fallback
            const displayText =
                propertyDefinition.propertyValues[lookup]?.displayText ??
                (option === null
                    ? t('i18n.configuration.defaultEmptySelection')
                    : lookup.toString());

            const displayImage = propertyDefinition.propertyValues[lookup]?.displayImage;
            return { question, displayText, displayImage };
        },
        [props.configuratorDefinition],
    );

    // Add display names for properties and options to maps for ease of access when converting between
    // what the components send back and what we need to send to the service
    const questions = Hooks.useMemo(() => {
        if (!props.existingPropertyOptions || !props.selections) {
            return null;
        }
        const selections = props.selections;
        const questions = Object.keys(props.existingPropertyOptions)
            .filter(q => props.configuratorDefinition.stylePropertyWhitelist.includes(q))
            .sort((a, b) =>
                props.configuratorDefinition.stylePropertyWhitelist.indexOf(a) <
                props.configuratorDefinition.stylePropertyWhitelist.indexOf(b)
                    ? -1
                    : 1,
            )
            .map(q => {
                const option = selections[q];

                const { question, displayText, displayImage } = getOptionDisplayDetails(q, option);

                return {
                    question,
                    propertyName: q,
                    currentOptionText: displayText,
                    content: displayImage,
                    focus: question === currentProperty,
                    disabled: false, // for first stage of development we're not disabling any questions
                } as QuestionCard.Props;
            });
        return questions;
    }, [props.selections, props.existingPropertyOptions, getOptionDisplayDetails, currentProperty]);

    const propertyOptions = Hooks.useMemo(() => {
        const { existingPropertyOptions, compatiblePropertyOptions, selections, priceDeltas } =
            props;
        if (
            currentProperty === null ||
            !existingPropertyOptions ||
            !compatiblePropertyOptions ||
            !selections
        )
            return null;

        return existingPropertyOptions[currentProperty].map(option => {
            // look for null string definition in configurator definition
            const { displayText, displayImage } = getOptionDisplayDetails(currentProperty, option);

            const priceDelta = priceDeltas[currentProperty]?.[option?.toString() ?? 'null'] ?? null;
            const current = selections[currentProperty] === option;

            const priceDeltaString =
                (priceDelta >= 0 ? '+' : '') +
                Price.toLocaleString(priceDelta ?? 0, locale, currency);

            const compatible = (compatiblePropertyOptions[currentProperty] ?? [null]).includes(
                option,
            );

            return {
                displayText,
                content: displayImage,
                value: option,
                priceDelta,
                priceDeltaString,
                compatible,
                current,
            };
        });
    }, [
        props.existingPropertyOptions,
        props.compatiblePropertyOptions,
        props.selections,
        props.priceDeltas,
        getOptionDisplayDetails,
        currentProperty,
    ]);

    const products = Hooks.useMemo(() => {
        return propertyOptions?.map(opt => {
            const {
                displayText,
                content,
                value,
                priceDelta,
                priceDeltaString,
                compatible,
                current,
            } = opt;
            return {
                optionText: displayText,
                propertyValue: value,
                content,
                compatible,
                active: current,
                loading: priceDelta === null && !current,
                priceDelta: priceDeltaString,
                selectedText: t('i18n.configuration.selected'),
            } as ConfigProductCard.Props;
        });
    }, [propertyOptions]);

    const loading = !(questions && props.selections);

    return (
        <CardPanel.Component
            products={products ?? []}
            questions={questions ?? []}
            isSafari={isSafari ?? false}
            incompatibleSelectionLoading={loading}
            loadingText={t('i18n.configuration.updatingConfiguration')}
            statusText={t('i18n.configuration.loadingStatus')}
            onSelectQuestion={(propertyName: string) => {
                const [category, type] = propertyName.split(':');
                dispatch(ConfigurationActions.selectQuestionAction({ category, type }));
                setActivePage(CardPanel.ActivePage.OPTIONS);
                setCurrentProperty(propertyName);
            }}
            title={
                activePage === ActivePage.QUESTIONS
                    ? props.configuratorDefinition.styleStepHeader
                    : currentProperty
                    ? props.configuratorDefinition.configurableProperties[currentProperty]
                          .displayName
                    : ''
            }
            undo={props.onUndo}
            redo={props.onRedo}
            activePage={activePage}
            redoText={t('i18n.global.redo')}
            undoText={t('i18n.global.undo')}
            done={
                activePage === ActivePage.OPTIONS
                    ? () => {
                          setCurrentProperty(null);
                          setActivePage(CardPanel.ActivePage.QUESTIONS);
                          props.hideAlert();
                      }
                    : undefined
            }
            doneText={t('i18n.global.done')}
            helperText={t('i18n.configuration.incompatible')}
            nullOptionText={t('i18n.configuration.emptySelection')}
            onSelectOption={(propertyValue: PropertyValue) => {
                if (currentProperty) {
                    if (currentProperty && propertyValue !== undefined) {
                        props.onUpdateSelection(currentProperty, propertyValue);
                    } else {
                        // TODO: handle this?
                    }
                }
            }}
            onOptionBecomeVisible={propertyValue => {
                currentProperty && props.requestPriceDelta(currentProperty, propertyValue);
            }}
        />
    );
}
